import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { X, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createLead } from "@/lib/api";
import { getAttribution } from "@/lib/utm";
import { trackLeadCreated } from "@/lib/ads";
import { trackDemoRequested } from "@/lib/analytics";

// Shown on the landing page and the live-agent page on every visit, including
// a reload — someone who already handed over their details is the one
// exception, because asking them again is just a worse version of thanking
// them. That is remembered per browser, so it survives the reload the way the
// prompt itself is meant to not.
const SUBMITTED_KEY = "ownpbx_lead_submitted";

// Long enough that the page is painted and read for a moment before anything
// covers it; short enough to still catch someone who bounces quickly.
const OPEN_DELAY_MS = 2500;

// Stored with the lead as proof of what was agreed to, so it has to stay
// word-for-word identical to the sentence rendered below.
const CONSENT_TEXT =
  "I agree that OwnPBX may contact me regarding this enquiry. Please see our Privacy Policy for details on how my information is handled.";

const hasSubmitted = () => {
  try {
    return localStorage.getItem(SUBMITTED_KEY) === "1";
  } catch {
    return false; // blocked storage: better to ask again than to never ask
  }
};

const markSubmitted = () => {
  try {
    localStorage.setItem(SUBMITTED_KEY, "1");
  } catch {
    /* nothing to do — worst case they see the form again next visit */
  }
};

const field =
  "w-full bg-transparent border-b border-white/15 focus:border-white outline-none py-2.5 text-white text-sm placeholder:text-neutral-500 transition-colors";

// A native <select> is drawn by the operating system, so the menu came up as a
// white Windows listbox in the middle of a dark modal. These use the site's
// own Select instead. The menu is portalled to <body>, so it needs to clear
// the popup's own z-80 overlay or it opens behind the backdrop.
const selectTrigger =
  "mt-1 h-auto rounded-none border-0 border-b border-white/15 bg-transparent px-0 py-2.5 text-sm text-white data-[placeholder]:text-neutral-500 focus:ring-0 focus:ring-offset-0 focus:border-white transition-colors";
const selectMenu =
  "z-[90] rounded-none border-white/10 bg-[#141417] text-white";
const selectItem =
  "text-sm text-neutral-300 focus:bg-white/10 focus:text-white";

export function LeadPopup({ context = "site" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "", website: "" });
  const [interest, setInterest] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const closeRef = useRef(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (hasSubmitted()) return;
    const t = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // Escape closes it. A prompt the visitor cannot dismiss is a trap, not a form.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.phone.trim()) {
      toast.error("Please add your name, company, work email and phone number.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      await createLead({
        ...form,
        interest,
        team_size: teamSize,
        source: "popup",
        page_url: window.location.href,
        consent_text: CONSENT_TEXT,
        marketing_consent: marketing,
        ...getAttribution(),
      });
      trackLeadCreated();
      trackDemoRequested("popup");
      markSubmitted();
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Deliberately not wrapped in AnimatePresence. On framer-motion 11 with
  // React 19 the exit animation runs but the child is never unmounted, which
  // left a full-screen invisible overlay swallowing every click on the page.
  // Closing instantly is a fair trade for a modal that definitely goes away.
  if (!open) return null;

  return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="lead-popup"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Book an OwnPBX demo"
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0d0d0f] border border-white/10 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.8)]"
          >
            {/* the same warm glow the featured cards use */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-40"
              style={{ background: "radial-gradient(ellipse 80% 100% at 50% 0%, rgba(255,150,40,0.22), rgba(255,150,40,0) 70%)" }}
            />

            <button
              ref={closeRef}
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="lead-popup-close"
              className="absolute right-3 top-3 z-10 grid place-items-center w-8 h-8 rounded-full text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X size={16} />
            </button>

            {done ? (
              <div className="relative px-7 py-14 text-center" data-testid="lead-popup-success">
                <CheckCircle2 size={44} className="mx-auto text-white" />
                <h3 className="mt-5 font-heading font-semibold text-2xl text-white">Thanks {form.name.split(" ")[0]}.</h3>
                <p className="mt-3 text-neutral-400 text-sm max-w-xs mx-auto leading-relaxed">
                  Our team will reach out shortly to set up your OwnPBX demo.
                </p>
                <button
                  onClick={() => setOpen(false)}
                  className="mt-7 rounded-full bg-white text-neutral-900 font-semibold text-sm px-7 py-2.5 hover:bg-neutral-200 transition-colors"
                >
                  Keep browsing
                </button>
              </div>
            ) : (
              <div className="relative px-7 py-8">
                <p className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[#5B8CFF]">Book a demo</p>
                <h3 className="mt-3 font-heading font-medium tracking-tight text-2xl md:text-3xl text-white leading-tight">
                  {context === "agents"
                    ? "Want this agent trained on your business?"
                    : "See OwnPBX handle a live call."}
                </h3>
                <p className="mt-2.5 text-neutral-400 text-sm leading-relaxed">
                  Leave your details and we'll set up a 15-minute walkthrough — inbound and outbound, in English, Hindi
                  and Hinglish.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="lead-popup-form">
                  {/* Honeypot: off-screen and skipped by keyboard and screen readers, so only bots fill it. */}
                  <input
                    type="text" name="website" value={form.website} onChange={upd("website")}
                    tabIndex={-1} autoComplete="off" aria-hidden="true"
                    style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input className={field} placeholder="Full name *" value={form.name} onChange={upd("name")} data-testid="popup-name" />
                    <input className={field} placeholder="Company name *" value={form.company} onChange={upd("company")} data-testid="popup-company" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input className={field} type="email" placeholder="Work email *" value={form.email} onChange={upd("email")} data-testid="popup-email" />
                    <input className={field} type="tel" placeholder="Phone number *" value={form.phone} onChange={upd("phone")} data-testid="popup-phone" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">Interested in</label>
                      <Select value={interest} onValueChange={setInterest}>
                        <SelectTrigger className={selectTrigger} data-testid="popup-interest">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className={selectMenu}>
                          {["AI Voice Agent", "Cloud PBX", "Both"].map((o) => (
                            <SelectItem key={o} value={o} className={selectItem}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">
                        Company size <span className="text-neutral-600">(optional)</span>
                      </label>
                      <Select value={teamSize} onValueChange={setTeamSize}>
                        <SelectTrigger className={selectTrigger} data-testid="popup-size">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className={selectMenu}>
                          {["1–10", "11–50", "51–200", "200+"].map((o) => (
                            <SelectItem key={o} value={o} className={selectItem}>{o}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <textarea
                    className={`${field} resize-none`} rows={2}
                    placeholder="What do you need help with? (optional)"
                    value={form.message} onChange={upd("message")} data-testid="popup-message"
                  />

                  <div className="space-y-2.5 pt-1">
                    <label className="flex items-start gap-2.5">
                      <input
                        type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 shrink-0 accent-white" data-testid="popup-consent"
                      />
                      <span className="text-[11px] leading-relaxed text-neutral-500">
                        I agree that OwnPBX may contact me regarding this enquiry. Please see our{" "}
                        <Link to="/privacy" target="_blank" className="underline underline-offset-2 hover:text-white">Privacy Policy</Link>{" "}
                        for details on how my information is handled.
                      </span>
                    </label>
                    {/* Promotional consent stays separate and optional: bundling it
                        into the line above would make neither a real choice. */}
                    <label className="flex items-start gap-2.5">
                      <input
                        type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)}
                        className="mt-0.5 shrink-0 accent-white" data-testid="popup-marketing-consent"
                      />
                      <span className="text-[11px] leading-relaxed text-neutral-500">
                        I would also like to receive occasional product news and offers from OwnPBX.{" "}
                        <span className="text-neutral-600">(Optional)</span>
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit" disabled={loading}
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-neutral-900 font-bold py-3.5 hover:bg-neutral-200 transition-colors disabled:opacity-60"
                    data-testid="popup-submit"
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <>Request my demo <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" /></>}
                  </button>
                  <button
                    type="button" onClick={() => setOpen(false)}
                    className="w-full text-center text-[11px] text-neutral-500 hover:text-neutral-300 transition-colors"
                    data-testid="popup-dismiss"
                  >
                    No thanks, I'm just looking
                  </button>
                </form>
              </div>
            )}
          </motion.div>
        </motion.div>
  );
}
